import { useState, type FormEvent, type ReactNode } from 'react';
import { useI18n } from '@/i18n/i18n-provider';
import { Alert, Button, Field } from '@/components/ui';

/**
 * Cosmetic only. `StubPaymentProvider` (billing-service) always succeeds and
 * there is nothing to charge yet, so nothing typed here is sent anywhere —
 * confirming calls straight through to `POST /auth/register`. Only reached
 * when the chosen module bundle actually costs something; a free bundle
 * skips straight from details to submission.
 */
export function PaymentStep({
  trialDays,
  isSubmitting,
  onConfirm,
  onBack,
  errorMessage,
}: {
  readonly trialDays: number;
  readonly isSubmitting: boolean;
  readonly onConfirm: () => void;
  readonly onBack: () => void;
  /** A failed submission's reason — shown here, since confirming is what triggers it. */
  readonly errorMessage: string | null;
}): ReactNode {
  const { t } = useI18n();
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    onConfirm();
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="auth-form">
      <h2 className="auth-title">{t('registration.payment.title')}</h2>
      <p className="auth-subtitle">
        {t('registration.payment.trialNotice', { values: { days: trialDays } })}
      </p>

      {errorMessage && <Alert tone="danger">{errorMessage}</Alert>}

      <Field
        label={t('registration.payment.cardNumber')}
        name="cardNumber"
        value={cardNumber}
        onChange={(event) => setCardNumber(event.target.value)}
        inputMode="numeric"
        placeholder="4242 4242 4242 4242"
        dir="ltr"
      />

      <div className="card-fields-row">
        <Field
          label={t('registration.payment.expiry')}
          name="expiry"
          value={expiry}
          onChange={(event) => setExpiry(event.target.value)}
          placeholder="MM/YY"
          dir="ltr"
        />
        <Field
          label={t('registration.payment.cvc')}
          name="cvc"
          value={cvc}
          onChange={(event) => setCvc(event.target.value)}
          inputMode="numeric"
          placeholder="CVC"
          dir="ltr"
        />
      </div>

      <Button type="submit" variant="primary" loading={isSubmitting}>
        {isSubmitting ? t('registration.payment.submitting') : t('registration.payment.confirm')}
      </Button>

      <span className="auth-secondary-link">{t('registration.payment.orDivider')}</span>

      <Button type="button" variant="secondary" disabled={isSubmitting} onClick={onConfirm}>
        {t('registration.payment.payWithPaypal')}
      </Button>

      <Button type="button" variant="ghost" disabled={isSubmitting} onClick={onBack}>
        {t('common.actions.back')}
      </Button>
    </form>
  );
}
